import React from "react";
import { ImportSteps, ValidationRow, SummaryStat } from "../../../../shared/components/ImportSteps";

interface ImportStepsMaterialProviderProps {
    currentStep: "file" | "validation" | "confirmation";
    selectedFile: File | null;
    importMode: "flush" | "update" | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onImportModeChange: (mode: "flush" | "update") => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    validatedRows: ValidationRow[];
    onBack: () => void;
    onImport: () => void;
    originalRowCount?: number;
}

export const ImportStepsMaterialProvider = (props: ImportStepsMaterialProviderProps) => {
    const { originalRowCount, validatedRows, ...restProps } = props;
    
    // Calculate summary stats
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    const totalRowsProcessed = originalRowCount || validatedRows.length;
    
    // Count all errors
    const totalErrorsCount = validatedRows.reduce((sum, row) => sum + (row.errors?.length || 0), 0);
    
    const summaryStats: SummaryStat[] = [
        {
            value: validRows.length,
            label: "Dòng hợp lệ",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: totalErrorsCount,
            label: "Lỗi",
            backgroundColor: totalErrorsCount > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: totalErrorsCount > 0 ? "#f44336" : "#4caf50",
            textColor: totalErrorsCount > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: invalidRows.length,
            label: "Dòng không hợp lệ",
            backgroundColor: invalidRows.length > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: invalidRows.length > 0 ? "#f44336" : "#4caf50",
            textColor: invalidRows.length > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: totalRowsProcessed,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const fileFormatInstructions = (
        <>
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>Lưu ý về định dạng file:</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Headers phải ở dòng 3</li>
                <li>Dữ liệu bắt đầu từ dòng 4</li>
                <li>Chỉ sử dụng sheet đầu tiên</li>
                <li>Trường bắt buộc: material_code (Mã vật tư), name (Tên vật tư)</li>
                <li>Trường tùy chọn: provider_code (Mã NCC) - nếu không có, vật tư sẽ được tạo mà không có quan hệ với nhà cung cấp</li>
                <li>provider_price (Giá nhập) - mặc định là 1 nếu không được cung cấp</li>
                <li>
                    Các trường tùy chọn: storage (Kho số), category (Hạng mục - NLVC/NLVP), 
                    measurement_unit (Đơn vị), threshold (Mức cảnh bảo tồn kho), note (Ghi chú)
                </li>
                <li>
                    Category: NLVC → "Vật tư chính", NLVP → "Vật tư phụ"
                </li>
                <li>
                    Nếu vật tư chưa tồn tại, hệ thống sẽ tự động tạo mới
                </li>
            </ul>
        </>
    );

    return (
        <ImportSteps
            {...restProps}
            validatedRows={validatedRows}
            title="Nhập quan hệ vật tư - nhà cung cấp"
            summaryStats={summaryStats}
            templateUrl="/templates/material_provider_import.xlsx"
            importModeLabels={{
                update: {
                    title: "Cập nhật và thêm mới",
                    description: "Quan hệ vật tư-nhà cung cấp mới sẽ được thêm vào. Quan hệ trùng sẽ được cập nhật với giá từ file.",
                },
                flush: {
                    title: "Xóa tất cả và nhập mới",
                    description: "Tất cả quan hệ vật tư-nhà cung cấp hiện có sẽ bị xóa vĩnh viễn và thay thế bằng dữ liệu từ file. Hành động này không thể hoàn tác.",
                    warning: "CẢNH BÁO",
                },
            }}
            fileFormatInstructions={fileFormatInstructions}
            previewColumns={[
                { key: "material_code", label: "Mã vật tư" },
                { key: "name", label: "Tên vật tư" },
                { key: "storage", label: "Kho" },
                { key: "category", label: "Hạng mục" },
                { key: "measurement_unit", label: "Đơn vị" },
                { key: "threshold", label: "Mức cảnh báo" },
                { key: "provider_code", label: "Mã NCC" },
                { key: "provider_name", label: "Tên NCC" },
                { key: "provider_price", label: "Giá nhập" },
                { key: "note", label: "Ghi chú" },
            ]}
        />
    );
};

